import React from 'react'
import { Wifi, WifiOff, RotateCw, Pause, LogOut, Trash2, Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { SectionCard, Fact, Spinner, NotAvailable } from '../ui'
import { TONE, WA_EXPLAIN, formatDateTime, formatRelative } from '../../lib/support'

const ACTIONS = [
  { key: 'restart', label: 'Reiniciar', icon: RotateCw, className: 'btn-primary' },
  { key: 'stop', label: 'Parar', icon: Pause, className: 'btn-secondary' },
  { key: 'logout', label: 'Desconectar', icon: LogOut, className: 'btn-secondary' },
  { key: 'delete', label: 'Excluir sessão', icon: Trash2, className: 'btn-secondary text-red-700' },
]

export const WhatsappTab = ({ view, loading, stateError, onAction, busyAction, failed }) => {
  const { isOwner } = useAuth()
  const m = view.model
  const wa = m.wa

  const facts = wa
    ? [
        ['Nome da sessão', wa.session],
        ['Engine', wa.engine],
        ['Status no banco', wa.db],
        ['Visto por último', wa.lastSeen ? `${formatDateTime(wa.lastSeen)} (${formatRelative(wa.lastSeen)})` : '—'],
        ['Proxy', wa.proxy || 'Nenhum proxy atribuído'],
        ['Status do proxy', wa.proxyValid === undefined ? '—' : wa.proxyValid ? 'Válido' : 'Inválido'],
      ]
    : []
  const stateLabel = wa === null ? 'NÃO CONFIGURADO' : wa?.state === 'UNREACHABLE' ? 'DESCONHECIDO' : wa?.state || 'DESCONHECIDO'
  const explain =
    wa === null
      ? 'O estúdio ainda não conectou o WhatsApp. Oriente-o a acessar Configurações → WhatsApp.'
      : WA_EXPLAIN[wa?.state] || 'O provedor informou um estado que este console não reconhece.'

  return (
    <div className="flex flex-col gap-4">
      <section className="card flex flex-col gap-3 p-4">
        {loading && wa === undefined ? (
          <Spinner />
        ) : stateError ? (
          <NotAvailable>Não foi possível carregar o estado do WhatsApp do estúdio. Tente novamente em instantes.</NotAvailable>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`flex items-center gap-2 rounded-md px-3.5 py-1.5 text-[13px] font-bold ${TONE[view.wa.tone].tag}`}>
                {view.wa.tone === 'ok' ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                {stateLabel}
              </span>
              <span className="flex-1 text-[13px] text-admin-600">{explain}</span>
            </div>
            {facts.length > 0 && (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
                {facts.map(([label, value]) => (
                  <Fact key={label} label={label}>
                    {value || '—'}
                  </Fact>
                ))}
              </div>
            )}
            {wa &&
              (isOwner ? (
                <div className="flex flex-col gap-2 border-t border-admin-200 pt-2">
                  <div className="flex flex-wrap gap-2">
                    {ACTIONS.map((a) => {
                      const Icon = a.icon
                      return (
                        <button
                          key={a.key}
                          type="button"
                          className={`btn ${a.className}`}
                          onClick={() => onAction(a.key)}
                          disabled={!!busyAction}
                        >
                          {busyAction === a.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
                          {a.label}
                        </button>
                      )
                    })}
                  </div>
                  <span className="text-xs text-admin-600">
                    Reiniciar reinicializa a sessão. Parar a pausa. Desconectar desvincula a conta e exige nova leitura
                    do QR code. Excluir remove a sessão por completo.
                  </span>
                </div>
              ) : (
                <span className="border-t border-admin-200 pt-2 text-xs text-admin-600">
                  As ações de sessão são exclusivas do proprietário. Para reiniciar, escale para um proprietário e informe o nome da sessão.
                </span>
              ))}
          </>
        )}
      </section>

      <SectionCard
        title="Mensagens com falha"
        aside={<span className="text-xs text-admin-500">Últimas 24 horas</span>}
        className="overflow-x-auto"
      >
        {failed.unavailable ? (
          <NotAvailable>O acompanhamento de mensagens com falha ainda não está disponível — depende do endpoint de mensagens com falha.</NotAvailable>
        ) : failed.list == null ? (
          <Spinner className="py-6" />
        ) : failed.list.length === 0 ? (
          <p className="text-[13px] text-admin-600">Nenhuma mensagem com falha nas últimas 24 horas.</p>
        ) : (
          <table className="data-table min-w-[640px]">
            <thead>
              <tr>
                <th>Horário</th>
                <th>Destinatário</th>
                <th>Tipo</th>
                <th>Erro</th>
                <th>Tentativas</th>
              </tr>
            </thead>
            <tbody>
              {failed.list.map((f, i) => (
                <tr key={f.id ?? i}>
                  <td className="whitespace-nowrap">{formatDateTime(f.time)}</td>
                  <td className="mono">{f.recipient}</td>
                  <td>{f.kind}</td>
                  <td className="text-red-800">{f.error}</td>
                  <td className="tabular-nums">{f.attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>
    </div>
  )
}
